export const MonacoDslPlugin = {
    languageId: 'marumbi-dsl',

    register() {
        const langId = this.languageId;
        if (monaco.languages.getLanguages().some(l => l.id === langId)) return;

        monaco.languages.register({ id: langId });

        // Define the rules once in a plain array to avoid recursion
        const commonRules = [
            [/super:/, 'keyword'],
            [/[a-zA-Z_][a-zA-Z0-9_-]*/, 'identifier'],
            [/\*/, 'operator'],
        ];

        monaco.languages.setMonarchTokensProvider(langId, {
            defaultToken: 'invalid',
            tokenPostfix: '.dsl',
            keywords: ['entity', 'user', 'group', 'index', 'deny', 'not', 'and', 'or', 'super'],
            operators: [':', ',', '|', '@', '*'],

            brackets: [
                { open: '{', close: '}', token: 'delimiter.curly' },
                { open: '[', close: ']', token: 'delimiter.square' },
                { open: '(', close: ')', token: 'delimiter.parenthesis' }
            ],

            tokenizer: {
                root: [
                    [/\/\/.*$/, 'comment'],
                    [/\b(entity|user|group|index|deny|not|and|or|super)\b/, 'keyword'],

                    // ULID regex matching spec.md
                    [/[0-7][0-9A-HJKMNP-TV-Z]{25}/, 'number.ulid'],

                    // Transition to subject state when seeing @user: or @group:
                    [/@(user|group):/, 'tag', '@subject'],

                    // Include common identifier/operator rules
                    { include: '@common' },

                    [/[{}[\]()]/, '@brackets'],
                    [/[:|,@*]/, 'delimiter'],
                    [/[ \t\r\n]+/, 'white'],
                ],

                // This state handles the content after the @user: prefix
                subject: [
                    { include: '@common' },
                    [/$/, '', '@pop'],    // Return to root at end of line
                    [/[:|,@*]/, 'delimiter', '@pop'], // Return to root if we hit another delimiter
                ],

                // Base rules used by both root and subject
                common: commonRules
            }
        });

        monaco.languages.setLanguageConfiguration(langId, {
            comments: { lineComment: '//' },
            brackets: [['{', '}'], ['[', ']'], ['(', ')']],
            autoClosingPairs: [
                { open: '{', close: '}' }, { open: '[', close: ']' },
                { open: '(', close: ')' }, { open: '"', close: '"' }
            ],
            surroundingPairs: [
                { open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' }
            ]
        });

        const baseRules = [
            { token: 'keyword', fontStyle: 'bold' }, //
            { token: 'number.ulid', foreground: '4EC9B0' }, // Teal
            { token: 'comment', fontStyle: 'italic' }, //
        ];

        // Light Theme Definition
        monaco.editor.defineTheme('marumbi-light', {
            base: 'vs',
            inherit: true,
            rules: [
                ...baseRules,
                { token: 'keyword', foreground: 'AF00DB' }, // Purple-ish keyword
                { token: 'tag', foreground: '001080' },
                { token: 'identifier', foreground: '0070C1' },
                { token: 'comment', foreground: '008000' }, // Green
            ],
            colors: { 'editor.background': '#ffffff' }
        });

        // Dark Theme Definition
        monaco.editor.defineTheme('marumbi-dark', {
            base: 'vs-dark',
            inherit: true,
            rules: [
                ...baseRules,
                { token: 'keyword', foreground: 'C586C0' }, // Pinkish keyword
                { token: 'tag', foreground: '9CDCFE' },
                { token: 'identifier', foreground: 'CE9178' },
                { token: 'comment', foreground: '6A9955' }, // Green
            ],
            colors: { 'editor.background': '#222e45' } // Matches Slate-900 for Pro look
        });

        // Inside register()
        const updateEditorTheme = () => {
            const isDark = document.documentElement.classList.contains('dark');

            if (monaco?.editor !== null && monaco?.editor !== undefined) {
                monaco.editor.setTheme(isDark ? 'marumbi-dark' : 'marumbi-light');
            }
        };

        // Listen for class changes on <html> (common for Tailwind dark mode)
        const observer = new MutationObserver(updateEditorTheme);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        // Initial check
        updateEditorTheme();

        console.log('✅ Marumbi DSL registered');
    },

    async setupDiagnostics(editor, dotNetHelper) {

        editor = editor || monaco;

        const model = editor.getModel();
        if (!model) return;

        const langId = this.languageId; // Capture locally
        let debounceTimer;

        const updateDiagnostics = async () => {
            const text = model.getValue();
            try {
                const parseResult = await dotNetHelper.invokeMethodAsync('ParseDslForMonaco', text);
                const markers = (parseResult.errors || []).map(err => ({
                    severity: monaco.MarkerSeverity.Error,
                    startLineNumber: err.line + 1,
                    startColumn: err.column + 1,
                    endLineNumber: err.line + 1,
                    // Better column handling
                    endColumn: model.getLineMaxColumn(err.line + 1),
                    message: err.message,
                    source: 'Marumbi DSL'
                }));

                monaco.editor.setModelMarkers(model, langId, markers);
            } catch (ex) {
                console.error('DSL diagnostics error:', ex);
            }
        };

        model.onDidChangeContent(() => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(updateDiagnostics, 300);
        });

        await updateDiagnostics();
    }
};

